/**
 * Build the multi-sheet organizer workbook from the registered import
 * specs. Single source of truth for both the committed handoff workbook
 * (`data/workbooks/event-planner-templates.xlsx`, written by
 * `scripts/build-organizer-workbook.ts`) and the in-browser "Download
 * planning workbook" link in the admin sidebar.
 *
 * Each spec contributes one sheet with the canonical four-row layout:
 *   Row 1 = purpose blurb (merged, italic gray)
 *   Row 2 = column headers (bold, filled — green if importReady, blue otherwise)
 *   Row 3 = column notes (italic gray, smaller)
 *   Row 4 = example data row (the spec's `example` field)
 *
 * The round-trip test asserts that for every registered spec, the
 * generated workbook's row-2 headers + row-4 example match the spec
 * exactly. Because the workbook is now generated FROM the specs, drift
 * is impossible by construction.
 */
import ExcelJS from "exceljs";
import { IMPORT_SPECS } from "@event-planner/shared/imports";

/**
 * Build the workbook in memory. Caller decides what to do with it:
 *   - admin runtime: workbook.xlsx.writeBuffer() -> Blob -> click trigger
 *   - workspace script: workbook.xlsx.writeFile(path)
 *
 * Conventions and per-sheet guidance live on the admin's
 * PlanningWorkbookPage rather than as a README sheet inside the .xlsx —
 * Excel renders single-column text rows as a table, which reads as
 * broken next to the actual data sheets.
 */
export function buildOrganizerWorkbook(): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Flat Rock Events — Organizer Handoff";
  wb.created = new Date();

  // Spec-backed sheets (Users / Event / Roster / Locations / Sessions / travel / ops).
  for (const spec of IMPORT_SPECS) {
    addDataSheet(wb, {
      name: spec.sheetName,
      purpose: spec.purpose,
      columns: spec.columns,
      example: spec.example as Record<string, string | number | null>,
      importReady: true,
    });
  }

  return wb;
}

interface AddSheetParams {
  name: string;
  purpose: string;
  columns: ReadonlyArray<{ key: string; note: string }>;
  example: Record<string, string | number | null>;
  importReady: boolean;
}

function addDataSheet(wb: ExcelJS.Workbook, spec: AddSheetParams): void {
  const ws = wb.addWorksheet(spec.name);

  // Row 1: purpose summary, merged across all columns
  ws.getCell("A1").value = spec.purpose;
  ws.getCell("A1").font = { italic: true, color: { argb: "FF666666" } };
  ws.mergeCells(1, 1, 1, spec.columns.length);

  // Row 2: column headers
  spec.columns.forEach((col, idx) => {
    const cell = ws.getRow(2).getCell(idx + 1);
    cell.value = col.key;
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: spec.importReady ? "FFD8F0DA" : "FFE8E8F0" },
    };
  });

  // Row 3: column notes (italic, gray, smaller)
  spec.columns.forEach((col, idx) => {
    const cell = ws.getRow(3).getCell(idx + 1);
    cell.value = col.note;
    cell.font = { italic: true, color: { argb: "FF888888" }, size: 10 };
    cell.alignment = { wrapText: true, vertical: "top" };
  });

  // Row 4: example data row
  spec.columns.forEach((col, idx) => {
    const cell = ws.getRow(4).getCell(idx + 1);
    const value = spec.example[col.key];
    if (value !== undefined && value !== null && value !== "") {
      cell.value = value as ExcelJS.CellValue;
      cell.font = { color: { argb: "FF555555" } };
    }
  });

  // Column widths — header length + 4, max 36
  ws.columns = spec.columns.map((col) => ({
    key: col.key,
    width: Math.min(36, Math.max(14, col.key.length + 4)),
  }));

  // Freeze the top three rows so example + new data stay scrollable beneath
  ws.views = [{ state: "frozen", ySplit: 3 }];

  // Tab color: green for import-ready, blue for reference
  ws.properties.tabColor = {
    argb: spec.importReady ? "FF4CAF50" : "FF7986CB",
  };
}
